// src/admin/pages/reservation/AddReservationDrawer.tsx
// Lets staff record a test drive request taken over the phone or in person —
// the same shape as the client site's "Book a Test Drive" form (see
// client/src/pages/common-components/Reservation/ReserveCarModal.tsx): an
// optional Brand -> Model -> specific in-stock vehicle picker, then contact
// details. Appears in the list exactly like a website submission and can
// then be Approved/Rejected as usual.
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, DirectionsCar as CarIcon } from '@mui/icons-material';
import { fetchActiveBrands, Brand } from '../brand/brandService';
import { fetchActiveCarModels } from '../carmodel/carModelService';
import { CarModel } from '../carmodel/carModel';
import { ProductService } from '../car/carService';
import { Product } from '../../../enums/product';
import { StaffReservationInput } from './reservationService';
import DrawerFooter from '../../components/DrawerFooter';

interface AddReservationDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: StaffReservationInput) => Promise<void>;
}

const EMPTY_CONTACT = {
  customerName: '',
  customerPhone: '',
  customerEmail: '',
  preferredDate: '',
  preferredTime: '',
  message: '',
};

const AddReservationDrawer: React.FC<AddReservationDrawerProps> = ({ open, onClose, onSubmit }) => {
  const [form, setForm] = useState(EMPTY_CONTACT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ── Brand / Model / specific-vehicle selection — mirrors the client's
  // "Book a Test Drive" flow. Narrowing is optional at every step. ──────────
  const [brands, setBrands] = useState<Brand[]>([]);
  const [models, setModels] = useState<CarModel[]>([]);
  const [vehicles, setVehicles] = useState<Product[]>([]);
  const [selectedBrandId, setSelectedBrandId] = useState('');
  const [selectedModelId, setSelectedModelId] = useState('');
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetchActiveBrands().then(setBrands).catch(() => setBrands([]));
  }, [open]);

  useEffect(() => {
    setSelectedModelId('');
    setSelectedVehicleId('');
    setModels([]);
    if (!selectedBrandId) return;
    setLoadingModels(true);
    fetchActiveCarModels(selectedBrandId)
      .then(setModels)
      .catch(() => setModels([]))
      .finally(() => setLoadingModels(false));
  }, [selectedBrandId]);

  useEffect(() => {
    setSelectedVehicleId('');
    setVehicles([]);
    if (!selectedBrandId || !selectedModelId) return;
    setLoadingVehicles(true);
    ProductService.getInStockByBrandModel(selectedBrandId, selectedModelId)
      .then(setVehicles)
      .catch(() => setVehicles([]))
      .finally(() => setLoadingVehicles(false));
  }, [selectedBrandId, selectedModelId]);

  const selectedBrand = brands.find((b) => b.id === selectedBrandId);
  const selectedModel = models.find((m) => m.id === selectedModelId);
  const selectedVehicle = vehicles.find((v) => v.id === selectedVehicleId);

  const handleChange = (field: keyof typeof EMPTY_CONTACT) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const resetAndClose = () => {
    if (saving) return;
    setForm(EMPTY_CONTACT);
    setSelectedBrandId('');
    setSelectedModelId('');
    setSelectedVehicleId('');
    setError('');
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.customerName.trim() || !form.customerPhone.trim()) {
      setError('Please enter the customer name and phone number.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await onSubmit({
        inquiryType: 'Test Drive Request',
        customerName: form.customerName.trim(),
        customerPhone: form.customerPhone.trim(),
        customerEmail: form.customerEmail?.trim() || undefined,
        preferredDate: form.preferredDate || undefined,
        preferredTime: form.preferredTime || undefined,
        message: form.message?.trim() || undefined,
        carId: selectedVehicle?.id,
        carName: selectedVehicle?.name,
        carImage: selectedVehicle?.images?.[0],
        carPrice: selectedVehicle?.price,
        brandId: !selectedVehicle ? selectedBrandId || undefined : undefined,
        brandName: !selectedVehicle ? selectedBrand?.name || undefined : undefined,
        modelId: !selectedVehicle ? selectedModelId || undefined : undefined,
        modelName: !selectedVehicle ? selectedModel?.name || undefined : undefined,
      });
      setForm(EMPTY_CONTACT);
      setSelectedBrandId('');
      setSelectedModelId('');
      setSelectedVehicleId('');
    } catch (err: any) {
      setError(err.message || 'Failed to add reservation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={resetAndClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: '100%', md: 600 }, maxWidth: 600 } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CarIcon color="primary" />
            <Typography variant="h6">Add Test Drive Request</Typography>
          </Box>
          <IconButton onClick={resetAndClose} size="small" disabled={saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Use this for a test drive request taken over the phone or in person — it will appear in the list
              exactly like a website submission and can be approved or rejected the same way.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Brand (optional)"
                  value={selectedBrandId}
                  onChange={(e) => setSelectedBrandId(e.target.value)}
                  disabled={saving}
                >
                  <MenuItem value="">Any brand</MenuItem>
                  {brands.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Model (optional)"
                  value={selectedModelId}
                  onChange={(e) => setSelectedModelId(e.target.value)}
                  disabled={saving || !selectedBrandId || loadingModels}
                  helperText={loadingModels ? 'Loading models…' : undefined}
                >
                  <MenuItem value="">Any model</MenuItem>
                  {models.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {selectedModelId && (
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Specific vehicle (optional)"
                    value={selectedVehicleId}
                    onChange={(e) => setSelectedVehicleId(e.target.value)}
                    disabled={saving || loadingVehicles}
                    helperText={loadingVehicles ? 'Loading in-stock units…' : undefined}
                  >
                    <MenuItem value="">Any available {selectedModel?.name || 'unit'}</MenuItem>
                    {vehicles.map((v) => {
                      const details = [
                        v.exteriorColor,
                        v.modelYear,
                        v.mileageKm != null ? `${v.mileageKm.toLocaleString()} km` : null,
                      ].filter(Boolean).join(' · ');
                      return (
                        <MenuItem key={v.id} value={v.id}>
                          {v.name}
                          {details ? ` (${details})` : ''}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Preferred Date"
                  type="date"
                  value={form.preferredDate}
                  onChange={handleChange('preferredDate')}
                  InputLabelProps={{ shrink: true }}
                  disabled={saving}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Preferred Time"
                  type="time"
                  value={form.preferredTime}
                  onChange={handleChange('preferredTime')}
                  InputLabelProps={{ shrink: true }}
                  disabled={saving}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Customer Name"
                  value={form.customerName}
                  onChange={handleChange('customerName')}
                  disabled={saving}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Phone Number"
                  value={form.customerPhone}
                  onChange={handleChange('customerPhone')}
                  disabled={saving}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email (optional)"
                  type="email"
                  value={form.customerEmail}
                  onChange={handleChange('customerEmail')}
                  helperText="If provided, the customer will get an email when this is approved or rejected."
                  disabled={saving}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Notes / Message (optional)"
                  value={form.message}
                  onChange={handleChange('message')}
                  disabled={saving}
                />
              </Grid>
            </Grid>
          </Box>

          <DrawerFooter>
            <Button onClick={resetAndClose} variant="outlined" disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {saving ? 'Adding...' : 'Add Test Drive Request'}
            </Button>
          </DrawerFooter>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AddReservationDrawer;
